import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const showingsApi = createApi({
  reducerPath: "showingsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Showings", "TourRequests"],
  endpoints: (builder) => ({
    // Get all showings
    getAllShowings: builder.query({
      query: (params) => ({
        url: "/showings",
        params,
      }),
      providesTags: ["Showings"],
    }),

    // Get today's showings
    getTodayShowings: builder.query({
      query: () => "/showings/today",
      providesTags: ["Showings"],
    }),

    // Get showings by date range (for calendar)
    getShowingsByDateRange: builder.query({
      query: ({ start, end }) => ({
        url: "/showings/calendar",
        params: { start, end },
      }),
      providesTags: ["Showings"],
    }),

    // Get single showing
    getShowingById: builder.query({
      query: (id) => `/showings/${id}`,
      providesTags: (result, error, id) => [{ type: "Showings", id }],
    }),

    // Create showing
    createShowing: builder.mutation({
      query: (data) => ({
        url: "/showings",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Showings"],
    }),

    // Update showing
    updateShowing: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/showings/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Showings", id },
        "Showings",
      ],
    }),

    // Update showing status
    updateShowingStatus: builder.mutation({
      query: ({ id, status }) => ({
        url: `/showings/${id}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Showings", id },
        "Showings",
      ],
    }),

    // Add feedback
    addShowingFeedback: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/showings/${id}/feedback`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Showings", id },
        "Showings",
      ],
    }),

    // Delete showing
    deleteShowing: builder.mutation({
      query: (id) => ({
        url: `/showings/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Showings"],
    }),

    // Request a tour of another agent's listing
    scheduleTour: builder.mutation({
      query: (data) => ({
        url: "/showings/tour-request",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Showings", "TourRequests"],
    }),

    // Incoming tour requests awaiting this agent's response
    getTourRequests: builder.query({
      query: () => "/showings/tour-requests",
      providesTags: ["TourRequests"],
    }),

    // Accept / decline an incoming tour request
    respondToTourRequest: builder.mutation({
      query: ({ id, action }) => ({
        url: `/showings/${id}/respond`,
        method: "PATCH",
        body: { action },
      }),
      invalidatesTags: ["Showings", "TourRequests"],
    }),
  }),
});

export const {
  useGetAllShowingsQuery,
  useGetTodayShowingsQuery,
  useGetShowingsByDateRangeQuery,
  useGetShowingByIdQuery,
  useCreateShowingMutation,
  useUpdateShowingMutation,
  useUpdateShowingStatusMutation,
  useAddShowingFeedbackMutation,
  useDeleteShowingMutation,
  useScheduleTourMutation,
  useGetTourRequestsQuery,
  useRespondToTourRequestMutation,
} = showingsApi;
